import { useEffect, useMemo, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft } from 'lucide-react'
import { legKey, type MarketMatch, type QuotedOutcome, type Slip, type WinnerSelection } from '@brochure/shared'
import { useGuest } from '@/hooks/useGuest'
import { useSlip } from '@/hooks/useSlip'
import { useFreshMatch, useStockbet, useWallet, type StockbetTab } from '@/hooks/useStockbet'
import { LocalizedLink } from '@/components/LocalizedLink'
import { LoadError } from '@/components/stoccopong/LoadError'
import { Toast, type ToastState } from '@/components/stoccopong/Toast'
import { WalletTicket } from '@/components/stoccopong/TournamentHeader'
import { MarketsTab } from '@/components/stockbet/MarketsTab'
import { MySlips } from '@/components/stockbet/MySlips'
import { OutrightsTab } from '@/components/stockbet/OutrightsTab'
import { LiveSlips } from '@/components/stockbet/LiveSlips'
import { SlipBar, SlipDock } from '@/components/stockbet/SlipBar'
import { SlipSheet } from '@/components/stockbet/SlipSheet'
import { MarketsSkeleton, OutrightsSkeleton, Skeleton } from '@/components/stockbet/Skeleton'
import { SegmentedTabs } from '@/components/ui/SegmentedTabs'
import { type DraftSlip, draftFromSlip, mergeDrafts, selectionCount, slipLimits, toggleSelection } from '@/lib/slip'
import { outrightNames } from '@/lib/stockbet'

const TABS: readonly StockbetTab[] = ['markets', 'outrights', 'mine']

/**
 * Stockbet di un torneo, nella variante notte: mercati con le quote sulle palette e la schedina (barra in basso), gli
 * antepost, le schedine dell'ospite. La vedono tutti; puntano solo gli iscritti (token ospite). La classifica sta nel torneo, sotto "monete".
 * Titolo e tab compaiono subito; finché i dati della tab non arrivano, al loro posto c'è lo scheletro.
 */
export function StockbetPage() {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const { guest } = useGuest(id)
  const { slip, setSlip, place, isPlacing } = useSlip(id, guest?.token)
  const [searchParams, setSearchParams] = useSearchParams()
  const requested = searchParams.get('tab')
  // Una tab sconosciuta (anche ?tab=fixtures o la vecchia ?tab=leaderboard) apre il palinsesto.
  const active: StockbetTab = TABS.includes(requested as StockbetTab) ? (requested as StockbetTab) : 'markets'
  const { markets, outrights, mySlips, slipQuotes, loadMore, isLive } = useStockbet(id, guest?.token, slip.legs.map(legKey), active)
  const storedWallet = useWallet(id, guest?.token)
  const freshMatch = useFreshMatch(id, guest?.token)
  const [isSlipOpen, setIsSlipOpen] = useState(false)
  const [toast, setToast] = useState<ToastState | null>(null)

  // Durante "Carica altro" restano le partite già mostrate (la risposta precedente, se è di questo torneo).
  const cachedFixtures = markets.data ?? (markets.previous?.tournament.id === id ? markets.previous : undefined)
  // Punteggi, stato e 1-2 sempre dall'ultima risposta arrivata: mai quelli vecchi di una copia in cache.
  const fixtures = useMemo(() => cachedFixtures && { ...cachedFixtures, matches: cachedFixtures.matches.map(freshMatch) }, [cachedFixtures, freshMatch])
  // Intestazione, portafoglio e limiti dalla risposta della tab aperta: la tab Antepost non chiede il palinsesto.
  // Cambiando tab resta l'ultima (stesso torneo e stesso ospite) finché non arriva l'altra: niente salto dell'intestazione.
  const fresh = fixtures ?? outrights.data
  const shellKey = `${id}|${guest?.token ?? ''}`
  const [lastShell, setLastShell] = useState<{ key: string; value: NonNullable<typeof fresh> }>()
  useEffect(() => {
    if (fresh) setLastShell({ key: shellKey, value: fresh })
  }, [fresh, shellKey])
  const shell = fresh ?? (lastShell?.key === shellKey ? lastShell.value : undefined)
  const error = active === 'outrights' ? outrights.error : markets.error
  // Pagina intera d'errore solo senza nulla da mostrare: se fallisce "Carica altro" restano le partite già viste.
  if (!shell && error) {
    return (
      <main id="main" className="mx-auto max-w-7xl px-4 pt-28 pb-section sm:px-6 lg:px-8">
        <LoadError error={error} className="text-ink">
          <LocalizedLink to="stoccoPongTournament" params={{ id: id! }} className="mt-4 inline-block font-body text-sm font-semibold text-sea-ink underline-offset-4 hover:underline">
            ← {t('stockbet.back')}
          </LocalizedLink>
        </LoadError>
      </main>
    )
  }
  // Saldo unico per torneo e ospite: mai quello vecchio di una pagina in cache.
  const wallet = storedWallet !== undefined ? storedWallet : shell?.guest
  // Chi non è iscritto vede tutte le tab tranne le proprie schedine (in attesa dei dati vale il token salvato).
  const tabs = TABS.filter((tab) => tab !== 'mine' || (shell ? wallet : guest))
  const shown: StockbetTab = tabs.includes(active) ? active : 'markets'
  const limits = shell && slipLimits(shell.tournament, wallet?.balance ?? 0)

  function selectTab(tab: StockbetTab) {
    const next = new URLSearchParams(searchParams)
    next.set('tab', tab)
    setSearchParams(next, { replace: true })
  }

  // Schedina svuotata dallo sheet (ultimo esito tolto o "Svuota"): lo sheet si chiude, non si riapre al prossimo tocco.
  function changeSlip(next: DraftSlip) {
    setSlip(next)
    if (selectionCount(next) === 0) setIsSlipOpen(false)
  }

  // "Rigioca con le nuove quote": la schedina annullata torna in bozza con le quote di allora (aggiunta a quella
  // eventualmente iniziata nel frattempo); lo sheet mostra "vecchia → nuova" e "Accetta le nuove quote e gioca".
  function replay(rejected: Slip) {
    setSlip((current) => mergeDrafts(current, draftFromSlip(rejected)))
    setIsSlipOpen(true)
  }

  // La bozza l'ha già svuotata `useSlip` (solo se non è cambiata durante l'invio). Una giocata live (`pending`)
  // non ha toast: la sua conferma la mostra la barra di `LiveSlips`, che compare con la rilettura delle schedine.
  function handlePlaced(placed: Slip) {
    setIsSlipOpen(false)
    if (placed.status !== 'pending') setToast({ tone: 'success', message: t('stockbet.slip.placed', { count: placed.totalStake }) })
    markets.reload()
    outrights.reload()
    mySlips.reload()
  }

  function toggleWinner(market: MarketMatch, selection: WinnerSelection) {
    const odds = market.odds?.[selection]
    if (odds === undefined || odds === null) return
    setSlip((prev) => toggleSelection(prev, { matchId: market.matchId, home: market.homeTeam.name, away: market.awayTeam.name }, { market: 'winner', selection, odds }))
  }

  // Un antepost in schedina: l'evento è il suo mercato; il nome della squadra o del giocatore va con l'esito.
  function toggleOutright(o: QuotedOutcome) {
    const odds = o.odds
    if (odds === null || !outrights.data) return
    const names = outrightNames(outrights.data)
    setSlip((prev) =>
      toggleSelection(prev, { matchId: null, home: '', away: '' }, { market: o.market, selection: o.selection, ...(o.subjectId && { subjectId: o.subjectId, subjectName: names[o.subjectId] }), odds }),
    )
  }

  return (
    <>
      <div aria-hidden="true" className="h-16 bg-paper md:h-20" />
      <main id="main" className="night min-h-dvh">
        <div className="mx-auto max-w-2xl px-4 pt-4 pb-section sm:px-6 lg:max-w-5xl">
          <LocalizedLink
            to="stoccoPongTournament"
            params={{ id: id! }}
            className="-ml-2 inline-flex min-h-11 max-w-full items-center gap-1.5 rounded-full px-2 font-body text-sm font-semibold text-paper/65 transition-colors hover:bg-paper/10 hover:text-paper"
          >
            <ArrowLeft className="size-4 shrink-0" aria-hidden="true" />
            {shell ? <span className="truncate">{shell.tournament.name}</span> : <Skeleton className="h-4 w-32 rounded-full" />}
          </LocalizedLink>

          <header className="mt-3 flex flex-wrap items-end justify-between gap-x-6 gap-y-4">
            <div className="min-w-0">
              <h1 className="hero-title text-[clamp(3rem,1.8rem+6vw,6rem)] text-paper">{t('stockbet.title')}</h1>
              <p className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 font-body text-sm text-paper/60">
                {t('stockbet.intro')}
                {isLive && shell?.tournament.status === 'active' && (
                  <span data-testid="live" className="inline-flex items-center gap-1.5 font-semibold uppercase tracking-[0.14em] text-coral">
                    <span className="size-2 rounded-full bg-coral motion-safe:animate-pulse" aria-hidden="true" />
                    {t('stoccoPong.live')}
                  </span>
                )}
              </p>
            </div>
            {wallet && <WalletTicket name={wallet.name} balance={wallet.balance} className="border-paper shadow-[3px_3px_0_var(--color-sun)]" />}
          </header>

          {shell && !wallet && (
            <p data-testid="stockbet-readonly" className="mt-6 rounded-2xl border border-paper/15 bg-paper/5 px-4 py-3 font-body text-sm text-paper/80">
              {t('stockbet.readOnly')}
            </p>
          )}

          {/* Tab sticky sotto l'header del sito: la fascia d'inchiostro copre il contenuto che scorre sotto. */}
          <div className="sticky top-16 z-20 -mx-4 mt-6 bg-ink/95 px-4 py-2 backdrop-blur-md sm:-mx-6 sm:px-6 md:top-20">
            <SegmentedTabs
              tabs={tabs.map((tab) => ({ key: tab, label: t(`stockbet.tabs.${tab}`) }))}
              active={shown}
              onSelect={selectTab}
              ariaLabel={t('stockbet.tabs.ariaLabel')}
              idPrefix="sb-tab"
              panelId={`sb-panel-${shown}`}
              testIdPrefix="sb-tab-"
              tone="night"
              fill
            />
          </div>

          <section role="tabpanel" id={`sb-panel-${shown}`} aria-labelledby={`sb-tab-${shown}`} className="mt-5">
            {shown === 'markets' &&
              (fixtures ? (
                <MarketsTab tournamentId={id!} data={fixtures} slip={slip} onToggle={toggleWinner} onLoadMore={markets.error ? markets.reload : loadMore} isLoadingMore={markets.isLoading} hasLoadError={!!markets.error} />
              ) : (
                <MarketsSkeleton />
              ))}
            {shown === 'outrights' &&
              (outrights.data ? (
                <OutrightsTab data={outrights.data} slip={slip} canPick={!!wallet} onPick={toggleOutright} />
              ) : outrights.error ? (
                <LoadError error={outrights.error} className="text-paper" />
              ) : (
                <OutrightsSkeleton />
              ))}
            {shown === 'mine' && (mySlips.data ? <MySlips data={mySlips.data} /> : <MarketsSkeleton />)}
          </section>
        </div>
        {wallet && guest && limits && (
          <>
            <SlipDock>
              <LiveSlips data={mySlips.data} onReplay={replay} />
              <SlipBar slip={slip} quotes={slipQuotes.data} limits={limits} onOpen={() => setIsSlipOpen(true)} />
            </SlipDock>
            {isSlipOpen && (
              <SlipSheet
                place={place}
                isPlacing={isPlacing}
                slip={slip}
                quotes={slipQuotes.data}
                quotesError={slipQuotes.error}
                onRetryQuotes={slipQuotes.reload}
                limits={limits}
                onChange={changeSlip}
                onClose={() => setIsSlipOpen(false)}
                onPlaced={handlePlaced}
                onStale={slipQuotes.reload}
              />
            )}
          </>
        )}
        {toast && <Toast toast={toast} onDismiss={() => setToast(null)} />}
      </main>
    </>
  )
}
